// ============================================================================
// Doc Order — Section 5 fee rules (README §5, spec A1.6–A1.8).
// Rule-for-rule port of the prototype's data.js DO_* fee helpers.
// Disposition and reconciliation are separate workflow legs. Their shared
// helpers drive row state, sign-off, and transition gates (never fork them).
// ============================================================================

import { DO_TOLERANCE } from './config.ts';
import type { DocOrderFee, FeeAck, FeeInvoice } from './types.ts';

/**
 * Does this fee still need a disposition? (data.js DO_feeNeedsDisposition)
 * A charged (>0), applicable fee needs action when ANY of:
 *  - unconfirmed auto_trust placeholder;
 *  - Credit Report refresh choice unanswered (refreshCredit === null);
 *  - split (2+ portions) with any portion empty/undisposed, or portions don't
 *    sum to the fee total (tolerance .01);
 *  - no paidBy or no timing.
 * $0 and notApplicable fees never need a disposition.
 */
export function feeNeedsDisposition(f: DocOrderFee | null | undefined): boolean {
  if (!f) return false;
  if (!(Number(f.amount) > 0)) return false;
  if (f.notApplicable) return false;
  if (f.source === 'auto_trust' && !f.confirmed) return true;
  if (f.refreshCredit !== undefined && f.refreshCredit === null) return true;
  const splits = Array.isArray(f.splits) ? f.splits : null;
  if (splits && splits.length >= 2) {
    const allocated = splits.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
    if (Math.abs((Number(f.amount) || 0) - allocated) >= DO_TOLERANCE.discrepancy) return true;
    return splits.some((s) => !(Number(s.amount) > 0) || !s.paidBy || !s.timing);
  }
  return !f.paidBy || !f.timing;
}

/** R4 #26 — the sign pad names WHY each fee blocks (Jennifer sat on an
 *  invisible Credit-Report refresh question). Returns the human reason, or
 *  null when the fee doesn't block. Branch-for-branch mirror of
 *  feeNeedsDisposition; the parity is TEST-PINNED (reason ⟺ blocked) so the
 *  copy can never drift from the gate. */
export function feeDispositionBlocker(f: DocOrderFee | null | undefined): string | null {
  if (!f) return null;
  if (!(Number(f.amount) > 0)) return null;
  if (f.notApplicable) return null;
  if (f.source === 'auto_trust' && !f.confirmed) return `${f.name} — confirm the placeholder fee`;
  if (f.refreshCredit !== undefined && f.refreshCredit === null) return `${f.name} — answer the refresh question`;
  const splits = Array.isArray(f.splits) ? f.splits : null;
  if (splits && splits.length >= 2) {
    const allocated = splits.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
    if (Math.abs((Number(f.amount) || 0) - allocated) >= DO_TOLERANCE.discrepancy) {
      return `${f.name} — split portions must add up to the fee total`;
    }
    if (splits.some((s) => !(Number(s.amount) > 0) || !s.paidBy || !s.timing)) {
      return `${f.name} — every split portion needs paid by + timing`;
    }
    return null;
  }
  if (!f.paidBy || !f.timing) return `${f.name} — set paid by + timing`;
  return null;
}

/**
 * Fresh-pull normalization (README §5.3 — PRODUCTION boundary rule).
 * Apply ONLY at a fee row's FIRST materialization (order create, or a new
 * §800 line appearing on a later refresh) — never over saved processor
 * decisions. PAC (any payer) → cleared so the processor must explicitly pick;
 * POC-by-lender → POC Lendwise; POC-by-borrower kept; $0 untouched.
 */
export function normalizeIncomingFee<T extends Pick<DocOrderFee, 'amount' | 'paidBy' | 'timing' | 'reimburse'>>(
  f: T
): T {
  if (!(Number(f.amount) > 0)) return f;
  if (f.timing === 'POC') {
    if (f.paidBy === 'Borrower') return { ...f, paidBy: 'Borrower', timing: 'POC', reimburse: false };
    return { ...f, paidBy: 'Lendwise', timing: 'POC', reimburse: false };
  }
  return { ...f, paidBy: null, timing: null, reimburse: false };
}

/**
 * THE single invoice-participation rule (round-23 #2): an entry counts only
 * when still marked current AND actually FILED in the eFolder. Legacy entries
 * (no uploadStatus) predate the P4 upload state machine and count as filed;
 * 'queued'/'failed' uploads NEVER move invoice math — an AI-read amount must
 * not change reconciliation before the eFolder write really happened.
 */
export function activeFiledInvoices(fee: Pick<DocOrderFee, 'invoices'>): NonNullable<DocOrderFee['invoices']> {
  return (fee.invoices || []).filter(
    (inv) => inv.markedCurrent !== false && (inv.uploadStatus === undefined || inv.uploadStatus === 'filed')
  );
}

/** Sum of FILED invoices still marked current (Mark-Current is a live preview). */
export function invoiceTotal(fee: Pick<DocOrderFee, 'invoices'>): number {
  return activeFiledInvoices(fee).reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0);
}

/** One eFolder mirror doc, as the aggregate's folder projection sees it. */
export interface MirrorDocLike {
  file: string;
  at: string | null;
  attachmentId: string | null;
}

/**
 * Round 3 (Jennifer #1) — the PURE mirror-adoption plan: which pre-existing
 * ACTIVE eFolder docs become fee.invoices entries (prototype parity — every
 * visible doc row is markable; the processor types the amount). Rules:
 *  • UNIQUE-FOLDER: a doc is adopted ONLY when exactly one fee maps to its
 *    folder — a shared folder (e.g. the generic 'Invoice' bucket) must never
 *    double-count one doc into several fees or make commit-marks fight;
 *  • idempotent ORDER-WIDE (R3.1 #2): an entry of ANY status on ANY fee
 *    already carrying the attachmentId blocks adoption everywhere — a doc
 *    moved between eFolder containers must never end up as entries under two
 *    fees (double-count + conflicting Mark-Current writes);
 *  • adopted entries are filed + marked current + amount:null (no AI read).
 * The caller owns persistence (updated_at-guarded, NO revision bump).
 */
export function planMirrorAdoptions(
  fees: ReadonlyArray<Pick<DocOrderFee, 'id' | 'feeKey' | 'invoices'>>,
  folderByFeeKey: Readonly<Record<string, { folder: string; docs: ReadonlyArray<MirrorDocLike> }>>
): Array<{ feeId: string; add: FeeInvoice[] }> {
  const feesPerFolder = new Map<string, number>();
  for (const fee of fees) {
    const folder = folderByFeeKey[fee.feeKey]?.folder;
    if (folder) feesPerFolder.set(folder, (feesPerFolder.get(folder) ?? 0) + 1);
  }
  // Order-wide claimed ids — built across ALL fees before any adoption.
  const claimed = new Set<string>();
  for (const fee of fees) {
    for (const inv of fee.invoices ?? []) {
      if (inv.attachmentId) claimed.add(inv.attachmentId);
    }
  }
  const plan: Array<{ feeId: string; add: FeeInvoice[] }> = [];
  for (const fee of fees) {
    const mapped = folderByFeeKey[fee.feeKey];
    if (!mapped || (feesPerFolder.get(mapped.folder) ?? 0) !== 1) continue;
    const add: FeeInvoice[] = [];
    for (const d of mapped.docs) {
      if (!d.attachmentId || claimed.has(d.attachmentId)) continue;
      claimed.add(d.attachmentId);
      add.push({
        uid: `efolder-${d.attachmentId}`,
        file: d.file,
        folder: mapped.folder,
        amount: null,
        receivedAt: d.at ?? null,
        markedCurrent: true,
        uploadStatus: 'filed',
        attachmentId: d.attachmentId,
        adopted: true,
      });
    }
    if (add.length) plan.push({ feeId: fee.id, add });
  }
  return plan;
}

/** |invoice total − fee amount| ≥ .01 (spec A1.7) — filed invoices only. */
export function invoiceHasDiscrepancy(fee: Pick<DocOrderFee, 'invoices' | 'amount'>): boolean {
  if (!activeFiledInvoices(fee).length) return false;
  return Math.abs(invoiceTotal(fee) - (Number(fee.amount) || 0)) >= DO_TOLERANCE.discrepancy;
}

export interface CostToCureCheck {
  /** max(amount − lastDisclosed, markedInvoiceTotal − lastDisclosed, 0). */
  costToCure: number;
  /** Either overage > .005 — disables Reconcile, forces Escalate. */
  overDisclosed: boolean;
  amountOver: boolean;
  invoicesOver: boolean;
  /** markedInvoiceTotal − amount (underfunded when > .005; invoice short when < −.005). */
  markedDelta: number;
}

/**
 * Two INDEPENDENT Cost-to-Cure tests vs lastDisclosed (README §5.6):
 * (1) fee.amount > lastDisclosed, (2) marked-current invoice total > lastDisclosed.
 * Either alone trips overDisclosed; the shown Cost to Cure is the larger overage.
 */
export function costToCureCheck(
  fee: Pick<DocOrderFee, 'invoices' | 'amount' | 'lastDisclosedLE'>
): CostToCureCheck {
  const amount = Number(fee.amount) || 0;
  const marked = invoiceTotal(fee);
  // R5 (review-4 finding 5): a NULL/undefined disclosure basis means UNKNOWN
  // — Number(null) is 0, which read every disclosure-less fee as
  // over-disclosed against $0. Unknown basis ⇒ never overDisclosed (this
  // helper is now authoritative for escalation-kind derivation).
  if (fee.lastDisclosedLE === null || fee.lastDisclosedLE === undefined) {
    return { costToCure: 0, overDisclosed: false, amountOver: false, invoicesOver: false, markedDelta: marked - amount };
  }
  const lastDisclosed = Number(fee.lastDisclosedLE);
  if (!Number.isFinite(lastDisclosed)) {
    return { costToCure: 0, overDisclosed: false, amountOver: false, invoicesOver: false, markedDelta: marked - amount };
  }
  const amountOverage = amount - lastDisclosed;
  const invoiceOverage = marked - lastDisclosed;
  const amountOver = amountOverage > DO_TOLERANCE.costToCure;
  const invoicesOver = invoiceOverage > DO_TOLERANCE.costToCure;
  return {
    costToCure: Math.max(amountOverage, invoiceOverage, 0),
    overDisclosed: amountOver || invoicesOver,
    amountOver,
    invoicesOver,
    markedDelta: marked - amount,
  };
}

/**
 * Auto-reconcile predicate (README §5.6): when the marked invoice total equals
 * lastDisclosed, the fee amount snaps to that total and reconciled=true. The
 * caller keys this off a per-fee invoice-total change (never a manual amount
 * edit — a manual edit CLEARS reconciled and re-opens the sheet).
 */
export function autoReconcileTarget(
  fee: Pick<DocOrderFee, 'invoices' | 'lastDisclosedLE'>
): number | null {
  const lastDisclosed = Number(fee.lastDisclosedLE);
  if (!Number.isFinite(lastDisclosed)) return null;
  const marked = invoiceTotal(fee);
  if (marked <= 0) return null;
  return Math.abs(marked - lastDisclosed) < DO_TOLERANCE.discrepancy ? marked : null;
}

/** Split allocation check: every portion positive + disposed, sum == total (tol .01). */
export function splitsValid(fee: Pick<DocOrderFee, 'amount' | 'splits'>): boolean {
  const splits = Array.isArray(fee.splits) ? fee.splits : null;
  if (!splits || splits.length < 2) return true;
  const allocated = splits.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  if (Math.abs((Number(fee.amount) || 0) - allocated) >= DO_TOLERANCE.discrepancy) return false;
  return splits.every((s) => Number(s.amount) > 0 && !!s.paidBy && !!s.timing);
}

const PAID_BY_VALUES = new Set(['Borrower', 'LO', 'Lendwise']);
const TIMING_VALUES = new Set(['PAC', 'POC']);
const REFRESH_CREDIT_VALUES = new Set(['included', 'not-required']);

/**
 * Value-level validation for an incoming fee patch — runs SERVER-SIDE BEFORE
 * the revision compare-and-bump so a forged/malformed payload can never bump
 * the revision or clear the signature via a child write that would only fail
 * at the DB CHECK constraints (round-9 review finding 1). Returns the first
 * problem as a message, or null when the patch is value-clean.
 */
export function validateFeePatchValues(patch: Record<string, unknown>): string | null {
  const enumCheck = (key: string, allowed: Set<string>): string | null => {
    if (!(key in patch)) return null;
    const v = patch[key];
    if (v === null) return null;
    return typeof v === 'string' && allowed.has(v) ? null : `Invalid value for '${key}'`;
  };
  const boolCheck = (key: string): string | null => {
    if (!(key in patch)) return null;
    return typeof patch[key] === 'boolean' ? null : `'${key}' must be a boolean`;
  };

  const enumErr =
    enumCheck('paidBy', PAID_BY_VALUES) ??
    enumCheck('timing', TIMING_VALUES) ??
    enumCheck('refreshCredit', REFRESH_CREDIT_VALUES);
  if (enumErr) return enumErr;
  const boolErr =
    boolCheck('reimburse') ?? boolCheck('notApplicable') ?? boolCheck('confirmed') ?? boolCheck('reconciled');
  if (boolErr) return boolErr;

  if ('amount' in patch) {
    const n = Number(patch.amount);
    if (!Number.isFinite(n) || n < 0) return 'Amount must be a non-negative number';
  }
  if ('name' in patch && (typeof patch.name !== 'string' || !patch.name.trim())) {
    return 'Fee name must be a non-empty string';
  }
  if ('payee' in patch && patch.payee !== null && typeof patch.payee !== 'string') {
    return 'Payee must be a string';
  }
  if ('splits' in patch && patch.splits !== null) {
    if (!Array.isArray(patch.splits)) return 'Splits must be a list of portions';
    for (const s of patch.splits as Array<Record<string, unknown>>) {
      if (!s || typeof s !== 'object') return 'Each split portion must be an object';
      const amt = Number(s.amount);
      if (!Number.isFinite(amt) || amt < 0) return 'Split amounts must be non-negative numbers';
      if (s.paidBy !== null && s.paidBy !== undefined && !(typeof s.paidBy === 'string' && PAID_BY_VALUES.has(s.paidBy))) {
        return 'Invalid split payer';
      }
      if (s.timing !== null && s.timing !== undefined && !(typeof s.timing === 'string' && TIMING_VALUES.has(s.timing))) {
        return 'Invalid split timing';
      }
      if (s.reimburse !== undefined && typeof s.reimburse !== 'boolean') return 'Split reimburse must be a boolean';
    }
  }
  if ('invoices' in patch) {
    if (!Array.isArray(patch.invoices)) return 'Invoices must be a list';
    for (const inv of patch.invoices as Array<Record<string, unknown>>) {
      if (!inv || typeof inv !== 'object') return 'Each invoice must be an object';
      if (typeof inv.uid !== 'string' || typeof inv.file !== 'string' || typeof inv.folder !== 'string') {
        return 'Invoice entries need uid/file/folder strings';
      }
      if (inv.amount !== null && inv.amount !== undefined) {
        const invAmt = Number(inv.amount);
        if (!Number.isFinite(invAmt) || invAmt < 0) return 'Invoice amounts must be non-negative numbers';
      }
      if (inv.markedCurrent !== undefined && typeof inv.markedCurrent !== 'boolean') {
        return 'Invoice markedCurrent must be a boolean';
      }
      // P4 upload state machine (round-23 #1) — optional, enum-validated.
      if (
        inv.uploadStatus !== undefined &&
        !(typeof inv.uploadStatus === 'string' && ['queued', 'filed', 'failed'].includes(inv.uploadStatus))
      ) {
        return 'Invoice uploadStatus must be queued, filed, or failed';
      }
      if (inv.queueId !== undefined && inv.queueId !== null && typeof inv.queueId !== 'string') {
        return 'Invoice queueId must be a string';
      }
      if (inv.attachmentId !== undefined && inv.attachmentId !== null && typeof inv.attachmentId !== 'string') {
        return 'Invoice attachmentId must be a string';
      }
      if (
        inv.aiConfidence !== undefined &&
        inv.aiConfidence !== null &&
        !(typeof inv.aiConfidence === 'string' && ['high', 'low'].includes(inv.aiConfidence))
      ) {
        return 'Invoice aiConfidence must be high or low';
      }
      if (inv.uploadError !== undefined && inv.uploadError !== null && typeof inv.uploadError !== 'string') {
        return 'Invoice uploadError must be a string';
      }
    }
  }
  if ('acks' in patch && (patch.acks === null || typeof patch.acks !== 'object' || Array.isArray(patch.acks))) {
    return 'Acks must be an object';
  }
  return null;
}

/** Is this a (non-review) appraisal fee? (drives Appraisal 1/2 bucket split). */
export function isAppraisalFee(name: string | null | undefined): boolean {
  const n = (name || '').toLowerCase();
  return /appraisal/.test(n) && !/appraisal review/.test(n);
}

/** Is this a trust / attorney review fee? — the §800 line an Encompass pull can
 *  already carry, which the `closingInTrust` auto_trust injection must NOT
 *  duplicate, and which is itself Reconcile-able (Jennifer bug #17). Matches the
 *  `suggestInvoiceFolder` "Invoice Trust Review" trigger. */
export function isTrustReviewFee(name: string | null | undefined): boolean {
  // Collapse runs of spaces/hyphens so "Trust  Review", "Trust-Review", and
  // "Trust - Review Fee" all match (Encompass label variants — Codex P2).
  return /trust review|attorney review/.test(String(name ?? '').toLowerCase().replace(/[\s-]+/g, ' '));
}

/** THE dedupe predicate for the closingInTrust auto_trust injection (#17): a
 *  MATERIAL Encompass-sourced trust/attorney-review line already exists, so the
 *  placeholder must NOT be injected. Deliberately narrow (Codex P1) — a manual
 *  row, a $0 line, or a not-applicable line must NEVER suppress the needed
 *  injection. */
export function isMaterialTrustReviewFee(
  fee: Pick<DocOrderFee, 'source' | 'amount' | 'notApplicable' | 'name'>
): boolean {
  return fee.source === 'encompass' && Number(fee.amount) > 0 && !fee.notApplicable && isTrustReviewFee(fee.name);
}

/** Encompass invoice Doc-Group buckets (ops-confirmed list). */
export const DO_INVOICE_FOLDERS = [
  'Insurance Invoice',
  'Insurance RCE',
  'Invoice',
  'Invoice Appraisal 1',
  'Invoice Appraisal 2',
  'Invoice AVM+PCI',
  'Invoice CDA',
  'Invoice Certificate of Insurance',
  'Invoice Credit',
  'Invoice Field Review',
  'Invoice HOA',
  'Invoice Survey',
  'Invoice Trust Review',
  'Invoice VOE',
] as const;

/**
 * Fee → suggested invoice folder (data.js DO_suggestInvoiceFolder; ops F5:
 * line 804 is ALWAYS Appraisal 1; other appraisal charges → Appraisal 2).
 * Unmapped fees fall back to the generic 'Invoice' folder.
 */
export function suggestInvoiceFolder(
  feeName: string | null | undefined,
  fee?: Pick<DocOrderFee, 'id' | 'line' | 'name'> | null,
  allFees?: ReadonlyArray<Pick<DocOrderFee, 'id' | 'name'>>
): string {
  const n = (feeName || '').toLowerCase();
  if (/appraisal review/.test(n)) return 'Invoice CDA';
  if (/field review/.test(n)) return 'Invoice Field Review';
  if (/avm/.test(n)) return 'Invoice AVM+PCI';
  if (/appraisal/.test(n)) {
    if (fee && fee.line) return String(fee.line) === '804' ? 'Invoice Appraisal 1' : 'Invoice Appraisal 2';
    const apprFees = Array.isArray(allFees) ? allFees.filter((f) => isAppraisalFee(f.name)) : [];
    if (fee && apprFees.length > 1) {
      const idx = apprFees.findIndex((f) => f.id === fee.id);
      return idx >= 1 ? 'Invoice Appraisal 2' : 'Invoice Appraisal 1';
    }
    return 'Invoice Appraisal 1';
  }
  // Route via the canonical predicate so "Trust-Review"/"Trust - Review"
  // variants land in the trust folder, not generic Invoice (Batch 3.1 F5 —
  // isTrustReviewFee collapses space/hyphen runs before matching).
  if (isTrustReviewFee(feeName)) return 'Invoice Trust Review';
  if (/eoi|cert.*insurance|certificate of ins/.test(n)) return 'Invoice Certificate of Insurance';
  if (/credit/.test(n)) return 'Invoice Credit';
  if (/hoa/.test(n)) return 'Invoice HOA';
  if (/survey/.test(n)) return 'Invoice Survey';
  if (/verification|voe/.test(n)) return 'Invoice VOE';
  return 'Invoice';
}

/** Fee-specific mirror evidence. A populated folder counts only when exactly
 * one fee maps to it. Shared folders remain visible in the eye popover, but
 * cannot satisfy or block a specific fee until the processor uploads/assigns
 * evidence or records a missing-invoice escalation. This matches the mirror
 * adoption ownership rule below and prevents an unrecoverable §5 state. */
export function invoiceFolderStatusForFees(
  fees: ReadonlyArray<Pick<DocOrderFee, 'id' | 'feeKey' | 'line' | 'name'>>,
  folderCounts: Readonly<Record<string, number>>
): Record<string, boolean> {
  const folderByFee = new Map<string, string>();
  const feesPerFolder = new Map<string, number>();
  for (const fee of fees) {
    const folder = suggestInvoiceFolder(fee.name, fee, fees);
    folderByFee.set(fee.feeKey, folder);
    feesPerFolder.set(folder, (feesPerFolder.get(folder) ?? 0) + 1);
  }
  return Object.fromEntries(
    fees.map((fee) => {
      const folder = folderByFee.get(fee.feeKey) ?? 'Invoice';
      return [fee.feeKey, (feesPerFolder.get(folder) ?? 0) === 1 && (folderCounts[folder] ?? 0) > 0];
    })
  );
}

/**
 * Section-6 closing-doc → eFolder container map (README §3). Missing
 * containers are created at upload time; unmapped docs fall back to a
 * container named after the document. Appraisal + Initial CD intentionally
 * absent (Appraisal isn't uploaded here; Initial CD clears via Field 3977).
 */
export const DO_DOC_EFOLDER_MAP: Record<string, string> = {
  'd-title': 'Title Commitment/Preliminary Title Report',
  // #10: 'd-vest' (Vesting Amendment) removed — no longer a §6 upload target.
  'd-cd': 'Estimated Settlement Statement',
  'd-eoi': 'Insurance - Subject',
  'd-hoamaster': 'HOA Master Insurance',
  'd-trustrev': 'Trust Review Letter',
};

/** Current-only view of an eFolder folder listing (historical revisions hidden). */
export function currentFolderDocs<T extends { status?: string | null }>(docs: T[] | null | undefined): T[] {
  return (docs || []).filter((d) => d.status === 'current');
}

/**
 * Server-side CLIENT-PATCH sanitizer for invoices + acks (round-25 P0): the
 * upload state machine is SERVER-OWNED — without this, a forged save-draft /
 * closing-edit-fee patch could inject a `uploadStatus:'filed'` entry (bypassing
 * the Docling/Claude audit gate entirely) or clear a standing
 * `acks.invoiceReject`. Rules:
 *   • invoices: the client can NEVER add or delete entries — entries are minted
 *     only by doc-order-upload. On EXISTING entries (matched by uid) the client
 *     may change ONLY `markedCurrent` (Mark-Current preview) and `amount`
 *     (AI-read correction — trust-but-correct); every other field is pinned to
 *     the server value.
 *   • acks: `invoiceReject` is server-owned — pinned to the current value in
 *     both directions (can't be set OR cleared by a client patch); the other
 *     ack keys (escalation/shortfall/encompassResolve) stay client-writable.
 * Returns a NEW patch object; untouched keys pass through.
 */
export function sanitizeClientFeePatch<T extends Partial<Pick<DocOrderFee, 'invoices' | 'acks'>> & Record<string, unknown>>(
  current: Pick<DocOrderFee, 'invoices' | 'acks'>,
  patch: T
): T {
  const out: Record<string, unknown> = { ...patch };
  if ('invoices' in patch) {
    const currentInvoices = current.invoices || [];
    const patched = Array.isArray(patch.invoices) ? patch.invoices : [];
    const byUid = new Map(patched.map((inv) => [inv?.uid, inv]));
    out.invoices = currentInvoices.map((cur) => {
      const p = byUid.get(cur.uid);
      if (!p) return cur; // client omission never deletes a server entry
      return {
        ...cur,
        ...(typeof p.markedCurrent === 'boolean' ? { markedCurrent: p.markedCurrent } : {}),
        ...(p.amount === null || Number.isFinite(Number(p.amount)) ? { amount: p.amount === null ? null : Number(p.amount) } : {}),
      };
    });
  }
  if ('acks' in patch && patch.acks && typeof patch.acks === 'object') {
    const currentReject = current.acks?.invoiceReject;
    const nextAcks: Record<string, unknown> = { ...(patch.acks as Record<string, unknown>) };
    if (currentReject) nextAcks.invoiceReject = currentReject; // can't clear
    else delete nextAcks.invoiceReject; // can't set
    // R5: escalations are SERVER-OWNED (they can waive the invoice workflow
    // requirement) — a generic client patch can neither mint, alter, nor
    // clear one. The edge's legacy-bundle bridge detects old-UI escalation
    // intents on the RAW patch BEFORE this sanitizer and routes them through
    // the typed server derivation.
    const currentEscalation = current.acks?.escalation;
    if (currentEscalation) nextAcks.escalation = currentEscalation; // can't clear/alter
    else delete nextAcks.escalation; // can't set
    out.acks = nextAcks;
  }
  return out as T;
}

/**
 * THE invoice-requirement rule (round-22 #6 — replaces every raw
 * `invoices.length` / any-entry check): a charged fee's requirement is
 * SATISFIED only by eFolder folder evidence or a FILED upload, and a standing
 * AI rejection HOLDS it open ("the invoice requirement stays open") until a
 * newer accepted upload clears invoiceReject.
 */
export function feeInvoiceRequirementOpen(
  fee: Pick<DocOrderFee, 'amount' | 'notApplicable' | 'invoices' | 'acks'>,
  folderHasCurrentDocs: boolean
): boolean {
  if (!(Number(fee.amount) > 0) || fee.notApplicable) return false;
  if (fee.acks?.invoiceReject) return true;
  return !(folderHasCurrentDocs || activeFiledInvoices(fee).length > 0);
}

/**
 * THE ack-currency rule (Batch 3.1) — one definition shared by the server
 * reconcile gate and the FeeTable/ReconcileSheet ack displays. An ack counts
 * ONLY when it is fully well-formed (acks are client-writable jsonb — every
 * field is runtime-validated, never trusted from the type) AND its recorded
 * delta matches the CURRENT filed-vs-amount delta within tolerance:
 *   - note / by / at: non-empty trimmed STRINGS (truthy junk rejected);
 *   - at: a parseable timestamp;
 *   - delta: strictly `typeof number` and finite (Number() coercion of
 *     strings/null/booleans is banned — NaN→0 previously matched a 0 delta).
 * Accepted residual (audit integrity, deferred): by/at remain client-supplied;
 * the server-authenticated event actor stays authoritative.
 */
export function isCurrentFeeAck(ack: FeeAck | null | undefined, currentDelta: number): boolean {
  if (!ack) return false;
  const isFilled = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;
  if (!isFilled(ack.note) || !isFilled(ack.by) || !isFilled(ack.at)) return false;
  if (!Number.isFinite(Date.parse(ack.at))) return false;
  if (typeof ack.delta !== 'number' || !Number.isFinite(ack.delta)) return false;
  return Math.abs(ack.delta - currentDelta) < DO_TOLERANCE.discrepancy;
}

/** Kind-aware escalation currency (R5, review-4 finding 2 + review-2 finding
 *  2): an escalation's delta is validated against ITS OWN basis —
 *  missing_invoice (and kind-less legacy) vs the invoice shortfall,
 *  cost_to_cure vs the current costToCure figure. Independent of
 *  invoiceHasDiscrepancy, so a zero-invoice escalation still reads current. */
export function isCurrentFeeEscalation(
  fee: Pick<DocOrderFee, 'invoices' | 'amount' | 'lastDisclosedLE'>,
  ack: FeeAck | null | undefined
): boolean {
  if (!ack) return false;
  const basis =
    ack.kind === 'cost_to_cure'
      ? costToCureCheck(fee).costToCure
      : invoiceTotal(fee) - (Number(fee.amount) || 0);
  return isCurrentFeeAck(ack, basis);
}

/**
 * The invoice WORKFLOW requirement (R5 #3 — Anthony: a current
 * missing_invoice escalation resolves the workflow; the EVIDENCE rule above
 * never changes, so the dot/tooltips keep reporting eFolder truth).
 * Precedence per reviews 3.4 + 4.3: eligibility lives in the evidence rule
 * ($0/notApplicable stay closed even with a stale reject); a standing
 * invoiceReject holds BOTH requirements open; only then can a CURRENT
 * SERVER-TYPED missing_invoice escalation close the workflow side.
 * cost_to_cure and kind-less legacy escalations NEVER waive.
 */
export function feeInvoiceWorkflowOpen(
  fee: Pick<DocOrderFee, 'amount' | 'notApplicable' | 'invoices' | 'acks' | 'lastDisclosedLE'>,
  folderHasCurrentDocs: boolean
): boolean {
  const evidenceOpen = feeInvoiceRequirementOpen(fee, folderHasCurrentDocs);
  if (!evidenceOpen) return false; // $0 / NA / real-evidence eligibility
  if (fee.acks?.invoiceReject) return true; // rejection holds BOTH open
  const esc = fee.acks?.escalation;
  return !(esc?.kind === 'missing_invoice' && isCurrentFeeEscalation(fee, esc));
}

/** Full processor reconciliation workflow. A charged fee remains open when
 * its invoice workflow is open, when no decision has been saved, or when a
 * persisted `reconciled` flag no longer has a current valid basis. Current
 * server-typed escalations close their own workflow; kind-less legacy acks do
 * not. This is the shared truth for the fee pill and §5 completion gate. */
export function feeReconciliationWorkflowOpen(
  fee: Pick<
    DocOrderFee,
    'amount' | 'notApplicable' | 'invoices' | 'acks' | 'lastDisclosedLE' | 'reconciled'
  >,
  folderHasCurrentDocs: boolean
): boolean {
  if (!(Number(fee.amount) > 0) || fee.notApplicable) return false;
  if (feeInvoiceWorkflowOpen(fee, folderHasCurrentDocs)) return true;
  const escalation = fee.acks?.escalation;
  const evidenceRequirementOpen = feeInvoiceRequirementOpen(fee, folderHasCurrentDocs);
  const currentTypedEscalation =
    (escalation?.kind === 'cost_to_cure' ||
      (escalation?.kind === 'missing_invoice' && evidenceRequirementOpen)) &&
    isCurrentFeeEscalation(fee, escalation);
  if (currentTypedEscalation) return false;
  // New evidence supersedes a missing-invoice escalation immediately. The
  // aggregate convergence pass will persist that clear, but transition gates
  // must not rely on the later write: validate the current invoice/shortfall
  // basis without allowing the stale escalation to stand in for it.
  const validationFee =
    escalation?.kind === 'missing_invoice' && !evidenceRequirementOpen
      ? { ...fee, acks: { ...(fee.acks ?? {}), escalation: undefined } }
      : fee;
  const validation = validateReconcileState(validationFee);
  return validation.basis === 'not_reconciled' || validation.basis === 'invalid';
}

/** Human detail for the sign pad when the reconciliation half of §5 blocks. */
export function feeReconciliationBlocker(
  fee: Pick<
    DocOrderFee,
    'name' | 'amount' | 'notApplicable' | 'invoices' | 'acks' | 'lastDisclosedLE' | 'reconciled'
  >,
  folderHasCurrentDocs: boolean
): string | null {
  if (!feeReconciliationWorkflowOpen(fee, folderHasCurrentDocs)) return null;
  return feeInvoiceWorkflowOpen(fee, folderHasCurrentDocs)
    ? `${fee.name} — add an invoice or escalate the missing invoice`
    : `${fee.name} — reconcile the invoice or escalate the discrepancy`;
}

/** SERVER-side reconcile gate (Jennifer bug #3; R5 reviews 3.6 + 5.4): a fee
 *  may only be marked `reconciled` on a REAL basis, and the basis is now
 *  DISCRIMINATED so supersession logic can require filed_match computed
 *  independently of a standing escalation (never circular). Valid bases:
 *   - filed_match: filed+marked invoices cover the amount;
 *   - shortfall: a current shortfall ack for the exact delta;
 *   - escalation: a CURRENT TYPED missing_invoice escalation ONLY — a forged
 *     reconciled:true riding a cost_to_cure or kind-less legacy escalation
 *     FAILS (CTC is an escalation, not a reconciliation; legacy acks are
 *     display-only).
 *  NB: an escalation/shortfall basis does NOT satisfy the evidence rule. */
export type ReconcileBasis = 'not_reconciled' | 'filed_match' | 'shortfall' | 'escalation' | 'invalid';
export interface ReconcileValidation {
  basis: ReconcileBasis;
  error: string | null;
}
export function validateReconcileState(
  fee: Pick<DocOrderFee, 'amount' | 'reconciled' | 'invoices' | 'acks' | 'lastDisclosedLE'>
): ReconcileValidation {
  if (fee.reconciled !== true) return { basis: 'not_reconciled', error: null };
  const amount = Number(fee.amount);
  if (!(amount > 0)) {
    return { basis: 'invalid', error: 'A fee cannot be reconciled at $0.00 — reduce is only valid against a filed invoice.' };
  }
  const filedTotal = invoiceTotal(fee); // filed + still-marked-current invoices
  if (filedTotal > 0 && Math.abs(filedTotal - amount) < DO_TOLERANCE.discrepancy) {
    return { basis: 'filed_match', error: null }; // the ordinary match
  }
  const currentDelta = filedTotal - amount;
  if (isCurrentFeeAck(fee.acks?.shortfall, currentDelta)) {
    return { basis: 'shortfall', error: null };
  }
  const esc = fee.acks?.escalation;
  if (esc?.kind === 'missing_invoice' && isCurrentFeeEscalation(fee, esc)) {
    return { basis: 'escalation', error: null };
  }
  return { basis: 'invalid', error: 'Reconcile needs a filed invoice matching the amount or a current shortfall/escalation note.' };
}

/** Invoice status dot for a fee row: na ($0/NA) | ok | missing (README §5.6).
 *  Routed through feeInvoiceRequirementOpen — filed-only + rejection-aware. */
export function feeInvoiceStatus(
  fee: Pick<DocOrderFee, 'amount' | 'notApplicable' | 'invoices' | 'acks'>,
  folderHasCurrentDocs: boolean
): 'na' | 'ok' | 'missing' {
  if (!(Number(fee.amount) > 0) || fee.notApplicable) return 'na';
  return feeInvoiceRequirementOpen(fee, folderHasCurrentDocs) ? 'missing' : 'ok';
}

export type { FeeInvoice };

/** R5 legacy bridge — classify the OLD bundle's escalation intent from a RAW
 *  (pre-sanitize) fee patch. The pre-R5 ReconcileSheet mutated escalations
 *  through generic compound patches: acknowledge SPREAD acks plus a NEW
 *  escalation; undo/reduce spread acks WITHOUT the escalation key; reconcile
 *  spread the stored escalation through UNCHANGED. The sanitizer now pins
 *  acks.escalation, so the edge detects the raw intent FIRST and applies it
 *  as a controlled mutation (server-derived kind / typed undo semantics).
 *  Pass-through (same note+at as stored) and patches without acks are null. */
export type LegacyEscalationIntent = { op: 'set'; note: string } | { op: 'remove' } | { op: 'passthrough' } | null;

export function detectLegacyEscalationIntent(
  storedEscalation: FeeAck | null | undefined,
  rawAcks: unknown
): LegacyEscalationIntent {
  if (!rawAcks || typeof rawAcks !== 'object') {
    return null;
  }
  const rawEsc = (rawAcks as { escalation?: unknown }).escalation;
  const note = rawEsc && typeof rawEsc === 'object' ? String((rawEsc as { note?: unknown }).note ?? '').trim() : '';
  const sameAsStored =
    !!storedEscalation &&
    !!rawEsc &&
    typeof rawEsc === 'object' &&
    (rawEsc as { note?: unknown }).note === storedEscalation.note &&
    (rawEsc as { at?: unknown }).at === storedEscalation.at;
  if (rawEsc && typeof rawEsc === 'object' && note && !sameAsStored) {
    return { op: 'set', note };
  }
  if (storedEscalation && (rawEsc === undefined || rawEsc === null)) {
    return { op: 'remove' };
  }
  if (sameAsStored) {
    // Round-10 #3: the old reconcile spreads the STORED escalation through
    // unchanged — the caller may still need to supersede it when the patch
    // carries a genuine filed-match reconcile.
    return { op: 'passthrough' };
  }
  return null;
}

/** Round-10 #3 — the legacy bridge's PERSISTED semantics for old-bundle
 *  compound patches touching a stored escalation. The old reduce-to-invoice
 *  sends reconciled:true while dropping the escalation key: that is a REAL
 *  filed-match reconcile and must keep reconciled while clearing the
 *  escalation (forcing false made the old UI toast "reconciled" over a
 *  fee stored unreconciled). Only an actual UNDO (no reconciled:true)
 *  clears both. A pass-through carrying reconciled:true with a REAL filed
 *  match supersedes the stored missing_invoice escalation at save time
 *  (legacy aggregate mode deliberately skips the supersession pass, so
 *  this is where old bundles get the equivalent truth). */
export function resolveLegacyEscalationBridge(args: {
  op: 'remove' | 'passthrough';
  rawReconciledTrue: boolean;
  storedKind: 'missing_invoice' | 'cost_to_cure' | undefined;
  filedMatch: boolean;
}): { clearEscalation: boolean; forceUnreconciled: boolean } {
  if (args.op === 'remove') {
    if (args.rawReconciledTrue) {
      // Old REDUCE-to-invoice: strip the escalation, PRESERVE the reconcile —
      // the standard merged-fee validation downstream enforces the filed
      // basis (an invalid basis still 422s the whole save; never false 200).
      return { clearEscalation: true, forceUnreconciled: false };
    }
    // Old UNDO: clear both, but reconciled only ever rode missing_invoice.
    return { clearEscalation: true, forceUnreconciled: args.storedKind === 'missing_invoice' };
  }
  // passthrough: supersede ONLY a real filed-match reconcile over a standing
  // missing_invoice escalation; anything else flows through untouched (an
  // escalation-basis reconcile remains valid old behavior).
  if (args.rawReconciledTrue && args.storedKind === 'missing_invoice' && args.filedMatch) {
    return { clearEscalation: true, forceUnreconciled: false };
  }
  return { clearEscalation: false, forceUnreconciled: false };
}
